/* eslint-disable indent */
import API from "../../api";
import urls from "../urls";

const { HOTSPOTS_LIST } = urls;

/**
 * HotspotApiService - Handles all Hotspot related API operations
 * Following Single Responsibility Principle
 */
class HotspotApiService {
  /**
   * Fetch hotspots using DRS service endpoint
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip (default: 0)
   * @param {number} params.limit - Number of records to fetch (default: 10)
   * @param {string} [params.search] - Search term for hotspot name
   * @returns {Promise<Object>} API response with hotspots data
   */
  async getHotspots(params = {}) {
    try {
      const { skip = 0, limit = 10, search } = params;

      // Build query parameters for the new endpoint
      const queryParams = new URLSearchParams();
      if (skip > 0) queryParams.append("skip", skip.toString());
      if (limit !== 10) queryParams.append("limit", limit.toString());
      if (search) queryParams.append("search", search);

      const apiUrl = queryParams.toString()
        ? `${HOTSPOTS_LIST}?${queryParams.toString()}`
        : HOTSPOTS_LIST;

      // Use custom headers matching the curl request
      const customHeaders = {
        accept: "application/json, text/plain, */*",
        authtype: "jwt",
      };

      const response = await API.Api(apiUrl).getCustomHeader(customHeaders);

      if (response.status && response.response?.data) {
        const responseData = response.response.data;

        // Handle both array and object with data property
        const hotspotsArray = Array.isArray(responseData)
          ? responseData
          : responseData.data || [];

        return {
          success: true,
          data: hotspotsArray,
          hasMore: hotspotsArray.length === limit,
          total: hotspotsArray.length,
        };
      }

      return {
        success: false,
        message: response.response?.data?.message || "Failed to fetch hotspots",
        data: [],
      };
    } catch (error) {
      console.error("Error fetching hotspots:", error);
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Network error while fetching hotspots",
        data: [],
      };
    }
  }

  /**
   * Get total count of hotspots for pagination
   * @param {string} [search] - Search term
   * @returns {Promise<number>} Total count
   */
  async getHotspotsCount(search) {
    const matchStage = search
      ? { hotspotName: { $regex: search, $options: "i" } }
      : {};

    const pipeline = [{ $match: matchStage }, { $count: "total" }];

    const requestBody = { pipeline };

    const customHeaders = {
      "Content-Type": "application/json",
      accept: "application/json, text/plain, */*",
    };

    const response = await API.Api(HOTSPOTS_LIST).postCustomHeader(
      requestBody,
      customHeaders
    );

    if (response.status && response.response?.data?.data?.[0]?.total) {
      return response.response.data.data[0].total;
    }

    throw new Error(
      response.response?.data?.message || "Failed to fetch hotspots count"
    );
  }

  /**
   * Search hotspots by name
   * @param {string} searchTerm - Search term
   * @param {Object} additionalParams - Additional parameters
   * @returns {Promise<Object>} Filtered hotspots response
   */
  async searchHotspots(searchTerm, additionalParams = {}) {
    return this.getHotspots({
      search: searchTerm,
      ...additionalParams,
    });
  }
}

// Export singleton instance
export const hotspotApiService = new HotspotApiService();
export default hotspotApiService;
